import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { eachDayOfInterval } from "date-fns";
import { Chart } from "@highcharts/react";
import type Highcharts from "highcharts/es-modules/masters/highcharts.src.js";
import "highcharts/es-modules/masters/modules/stock.src.js";
import { useTranslation } from "react-i18next";
import { DevErrorDisplay } from "../../../components/core/blocks/DevErrorDisplay";
import Loading from "../../../components/core/blocks/Loading";
import { useAppSelector } from "../../../redux/hooks";
import { formatCurrency } from "../../../utils/formatNumber";
import getMetricSpec from "../../merchandising/utils/metrics";
import { useGetV4CategoryReportByDate } from "../../perf_reports/api/category/v4CategoryReportByDate";
import { useDateFormatter } from "../../../i18n/useDateFormatter";

export function AdSpendChart({
  startDate,
  endDate,
  dailyAdSpendGoal,
  daysInPeriod,
  categoryId,
}: {
  startDate: Date;
  endDate: Date;
  dailyAdSpendGoal: number;
  daysInPeriod: number;
  categoryId?: number;
}) {
  const { t } = useTranslation(["goals", "metrics"]);
  const marketPlace = useAppSelector((state) => state.marketPlace.marketPlace);
  const adSpendGoalGraph = Array.from({ length: daysInPeriod }, (v, i) => [
    dailyAdSpendGoal * i * 0.9,
    dailyAdSpendGoal * i * 1.1,
  ]);

  const adSpendMetric = getMetricSpec("ad_spend");
  const adSpendMetricName = t(($) => $.metrics.byId.ad_spend.name);

  const filters = useMemo(() => {
    const categoryFilter = categoryId ? [{ id: "category_id", value: { op: "=", value: categoryId } }] : [];
    return [...categoryFilter];
  }, [categoryId]);

  const byDateQuery = useQuery(
    useGetV4CategoryReportByDate()({
      start: startDate,
      end: endDate,
      group: "day",
      preStart: startDate,
      preEnd: startDate,
      filters,
    }),
  );
  const axisDate = useDateFormatter("axis");

  if (byDateQuery.isLoading) {
    return <Loading fullPage={false} height={300} />;
  }

  if (!byDateQuery.isSuccess) {
    return <DevErrorDisplay userMessage={t(($) => $.goals["Error loading stats"])} error={byDateQuery.error} />;
  }

  const options: Highcharts.Options = {
    title: { text: "" },
    chart: {
      type: "line",
      height: 300,
      style: {
        fontFamily: "Poppins",
        fontWeight: "300",
        fontSize: "15px",
      },
    },
    tooltip: {
      formatter() {
        return `<span style="color:${this.series.color as string}">${this.series.name}</span>: <b>${formatCurrency(
          this.cumulativeSum,
          {
            marketPlace: marketPlace,
          },
        )}</b>`;
      },
    },
    xAxis: {
      categories: eachDayOfInterval({
        start: startDate,
        end: endDate,
      }).map((date) => axisDate.format(date)),
      labels: { step: 4 },
    },
    yAxis: {
      title: {
        text: undefined,
      },
    },
    series: [
      {
        name: t(($) => $.goals["{metricName} Expected"], { metricName: adSpendMetricName }),
        type: "arearange",
        data: adSpendGoalGraph,
        color: adSpendMetric.color,
        fillOpacity: 0.4,
        lineWidth: 0,
        marker: {
          enabled: false,
        },
        enableMouseTracking: false,
        zIndex: -1, // Ensure this series is below the line series
      },
      {
        name: adSpendMetricName,
        data: byDateQuery.data.data.slice(0, daysInPeriod).map(adSpendMetric.accessor),
        type: "line",
        cumulative: true,
        color: adSpendMetric.color,
      },
    ],
  };

  return <Chart options={options} />;
}
